import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Snowflake,
  ChevronRight,
  Fan,
  Gauge,
  Pipette,
  Settings,
  Timer,
  FileSpreadsheet,
  ShieldCheck,
  Phone,
  ArrowRight,
  Search,
  ThermometerSnowflake,
  Wrench
} from 'lucide-react';
import img from "../../image2/plategasket.webp";
import img1 from "../../image2/tit.jpg";
import img2 from "../../image2/flow.jpeg";
import img3 from "../../image2/fill.jpg";
import img4 from "../../image2/noz.avif";
import img5 from "../../image2/drif.jpeg";
import { useNavigate } from 'react-router-dom';
const CoolingSpares = () => {
  const [activeCategory, setActiveCategory] = useState('heat-exchangers');
  const navigate = useNavigate();
  const categories = [
    {
      id: 'heat-exchangers',
      title: 'Heat Exchangers',
      icon: <ThermometerSnowflake className="w-5 h-5" />,
      description: 'Heat exchanger components and plates',
      products: [
        {
          title: 'Plate Heat Exchanger Gaskets',
          description: 'High-temperature resistant gaskets for plate heat exchangers.',
          image: img,
          inStock: true,
          price: 'NPR',
          specs: 'NBR/EPDM Available'
        },
        {
          title: 'Titanium Heat Exchanger Plates',
          description: 'Corrosion-resistant titanium plates for harsh environments.',
          image: img1,
          inStock: true,
          price: 'NPR',
          specs: 'Grade 1 Titanium'
        },
        {
          title: 'Flow Distribution Plates',
          description: 'Optimized flow distribution plates for maximum efficiency.',
          image: img2,
          inStock: true,
          price: 'NPR',
          specs: '316L SS'
        }
      ]
    },
    {
      id: 'cooling-towers',
      title: 'Cooling Towers',
      icon: <Snowflake className="w-5 h-5" />,
      description: 'Tower components and fill material',
      products: [
        {
          title: 'Fill Media Pack',
          description: 'High-performance PVC fill media for optimal heat transfer.',
          image: img3,
          inStock: true,
          price: 'NPR',
          specs: 'UV Protected PVC'
        },
        {
          title: 'Distribution Nozzles',
          description: 'Precision water distribution nozzles for even coverage.',
          image: img4,
          inStock: true,
          price: 'NPR',
          specs: 'ABS Plastic'
        },
        {
          title: 'Drift Eliminators',
          description: 'High-efficiency drift eliminators for water conservation.',
          image: img5,
          inStock: true,
          price: 'NPR',
          specs: '99.99% Efficiency'
        }
      ]
    },
    
  ];

  const activeProducts = categories.find(cat => cat.id === activeCategory)?.products || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <motion.section 
        className="relative min-h-[400px] bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Animated Cold Pattern Background */}
        <div className="absolute inset-0 opacity-10">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute inset-0"
              initial={{ backgroundPositionX: "0%" }}
              animate={{ backgroundPositionX: "100%" }}
              transition={{
                duration: 15 + i * 3,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 1200 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 24c120 0 120 90 240 90s120-90 240-90 120 90 240 90 120-90 240-90 120 90 240 90V0H0v24z' fill='%23ffffff'/%3E%3C/svg%3E\")",
                backgroundRepeat: "repeat-x",
                backgroundSize: "1200px 120px",
                top: `${i * 25}%`,
                opacity: 0.2 - i * 0.03
              }}
            />
          ))}
        </div>

        <div className="container mx-auto px-6 h-full flex flex-col justify-center relative z-10 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                animate={{ 
                  rotate: [0, 180, 360],
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  duration: 4,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                <Snowflake className="w-12 h-12 text-blue-300" />
              </motion.div>
              <h1 className="text-4xl md:text-5xl font-bold">
                Cooling Water Spares
              </h1>
            </div>
            <p className="text-lg md:text-xl mb-8 max-w-2xl text-slate-200">
              Industrial-grade replacement parts for cooling water systems and heat exchangers
            </p>
            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-all duration-300 flex items-center gap-2"
              onClick={()=>navigate('/contact')}>
                <FileSpreadsheet className="w-5 h-5" />
                Technical Specs
              </button>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Features Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: <ShieldCheck />, title: 'OEM Quality', text: 'Manufacturer certified parts' },
              { icon: <Timer />, title: 'Fast Response', text: '24-hour emergency dispatch' },
              { icon: <Timer />, title: 'Installation', text: 'On-site technical support' },
              { icon: <Gauge />, title: 'Performance', text: 'Guaranteed efficiency' }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="text-blue-600 mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
          >
            <h2 className="text-3xl font-bold mb-4">Replacement Parts</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              High-quality spare parts for industrial cooling water systems
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-4 justify-center mb-12">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-50'
                }`}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category.icon}
                {category.title}
              </motion.button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {activeProducts.map((product, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
                whileHover={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm ${
                    product.inStock ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    {product.inStock ? 'In Stock' : 'Out of Stock'}
                  </div>
                  <div className="absolute bottom-4 left-4 bg-white/90 px-3 py-1 rounded-lg text-blue-900 font-bold">
                    {product.price}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-2">{product.title}</h3>
                  <p className="text-gray-600 mb-3">{product.description}</p>
                  <div className="bg-slate-50 rounded-lg p-2 mb-4 text-sm text-slate-700">
                    <span className="font-semibold">Specs:</span> {product.specs}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CoolingSpares;